import { AxiosProgressEvent, CancelTokenSource } from 'axios';

import BaseAPI from 'utils/api/base';

import { ActivityListResponse as VatixActivityListResponse, MessageActivityResponse, OpenRequest } from 'ui/types';

import {
  QueryParams,
  PaginationParams,
  ActivityResponse,
  APIRequest,
  OrderingParams,
  PaginatedResponse,
} from 'utils/api/types';

import { ResolutionReasonFormValues } from 'containers/ResolutionDialog/types';

import { UuidableLocationRedAmberAlert } from 'containers/AlarmDetails/components/AlarmLocation/types';

import {
  ARCListResponse,
  AlarmResponse,
  AlarmDetailsResponse,
  AlarmsPaginatedResponse,
  PendingRedAlertResponse,
  ResolutionReasonResponse,
  CompletedRedAlertResponse,
  EscalationProfileCreateARC,
  AmberAlarmPaginatedResponse,
  AlarmResponseProfileResponse,
  EscalationProfileDetailsResponse,
  AlarmResponseProfileSearchResponse,
  EscalationProfilePaginatedResponse,
} from './alarms.types';

class AlarmsAPI extends BaseAPI {
  loadArcs = (
    params?: Partial<PaginationParams> & { organisation?: string },
    cancelTokenSource?: CancelTokenSource
  ): APIRequest<ARCListResponse> => {
    const url = '/arcs/';
    const method = 'GET';

    return this.request<ARCListResponse>({
      url,
      method,
      params,
      cancelTokenSource,
    });
  };

  exportAlarm = (incidentId: string, params: OrderingParams | QueryParams): OpenRequest => {
    const url = `/api/alarms/${incidentId}/export/pdf/`;

    return this.open({
      url,
      params,
    });
  };

  loadAlarms = (params: OrderingParams | QueryParams): APIRequest<AlarmsPaginatedResponse> => {
    const url = '/alarms/';
    const method = 'GET';

    return this.request<AlarmsPaginatedResponse>({
      url,
      method,
      params,
    });
  };

  loadAmberAlarms = (params: OrderingParams | QueryParams): APIRequest<AmberAlarmPaginatedResponse> => {
    const url = '/alarms/timed/';
    const method = 'GET';

    return this.request<AmberAlarmPaginatedResponse>({
      url,
      method,
      params,
    });
  };

  loadAlarmDetails = (alarmId: string): APIRequest<AlarmDetailsResponse> => {
    const url = `/alarms/${alarmId}/`;
    const method = 'GET';

    return this.request<AlarmDetailsResponse>({
      url,
      method,
    });
  };

  searchAlarmResponseProfiles = (
    params?: Partial<{ query: string; organisation: string; limit: number }>
  ): APIRequest<AlarmResponseProfileSearchResponse[]> => {
    const url = '/alarms/escalation-profiles/search/';
    const method = 'GET';

    return this.request<AlarmResponseProfileSearchResponse[]>({
      url,
      method,
      params,
    });
  };

  loadAlarmLocations = (alarmId: string): APIRequest<PaginatedResponse<UuidableLocationRedAmberAlert>> => {
    const url = `/alarms/${alarmId}/locations/`;
    const method = 'GET';

    return this.request<PaginatedResponse<UuidableLocationRedAmberAlert>>({
      url,
      method,
      params: {
        limit: 10000,
      },
    });
  };

  addAlarmMessage = (alarmId: string, message: string): APIRequest<MessageActivityResponse> => {
    const url = `/alarms/${alarmId}/comments/`;
    const method = 'POST';

    return this.request<MessageActivityResponse>({
      url,
      method,
      data: { message },
    });
  };

  updateAlarmMessage = (alarmId: string, messageId: string, message: string): APIRequest<MessageActivityResponse> => {
    const url = `/alarms/${alarmId}/comments/${messageId}/`;
    const method = 'PUT';

    return this.request<MessageActivityResponse>({
      url,
      method,
      data: { message },
    });
  };

  removeAlarmMessage = (alarmId: string, messageId: string): APIRequest<MessageActivityResponse> => {
    const url = `/alarms/${alarmId}/comments/${messageId}/`;
    const method = 'DELETE';

    return this.request<MessageActivityResponse>({
      url,
      method,
    });
  };

  uploadAlarmFile = (
    alarmId: string,
    file: File,
    onUploadProgress: (event: AxiosProgressEvent) => void,
    cancelTokenSource: CancelTokenSource
  ): APIRequest<ActivityResponse> => {
    const method = 'POST';
    const url = `/alarms/${alarmId}/activities/files/`;

    const data = new FormData();
    data.append('file', file);

    return this.request<ActivityResponse>({
      url,
      method,
      data,
      onUploadProgress,
      cancelTokenSource,
    });
  };

  removeAlarmFile = (alarmId: string, fileId: string): APIRequest<void> => {
    const method = 'DELETE';
    const url = `/alarms/${alarmId}/activities/files/${fileId}/`;

    return this.request<void>({
      url,
      method,
    });
  };

  loadAlarmActivities = (alarmId: string, params: PaginationParams): APIRequest<VatixActivityListResponse> => {
    const method = 'GET';
    const url = `/alarms/${alarmId}/activities/`;

    return this.request<VatixActivityListResponse>({
      url,
      method,
      params,
    });
  };

  createEscalationProfiles = (data: EscalationProfileCreateARC): APIRequest<AlarmResponseProfileResponse> => {
    const method = 'POST';
    const url = `/alarms/escalation-profiles/`;

    return this.request<AlarmResponseProfileResponse>({
      url,
      method,
      data,
    });
  };

  loadEscalationProfiles = (params: OrderingParams | QueryParams): APIRequest<EscalationProfilePaginatedResponse> => {
    const method = 'GET';
    const url = `/alarms/escalation-profiles/`;

    return this.request<EscalationProfilePaginatedResponse>({
      url,
      method,
      params,
    });
  };

  loadEscalationProfileDetails = (escalationProfileId: string): APIRequest<AlarmResponseProfileResponse> => {
    const method = 'GET';
    const url = `/alarms/escalation-profiles/${escalationProfileId}/`;

    return this.request<AlarmResponseProfileResponse>({
      url,
      method,
    });
  };

  deleteAlarmResponseProfile = (escalationProfileId: string): APIRequest<void> => {
    const url = `/alarms/escalation-profiles/${escalationProfileId}/`;
    const method = 'DELETE';

    return this.request<void>({
      url,
      method,
    });
  };

  updateEscalationProfileDetails = (
    data: EscalationProfileDetailsResponse
  ): APIRequest<AlarmResponseProfileResponse> => {
    const method = 'PATCH';
    const url = `/alarms/escalation-profiles/${data.uuid}/`;

    return this.request<AlarmResponseProfileResponse>({
      url,
      method,
      data,
    });
  };

  loadPendingRedAlerts = (): APIRequest<PendingRedAlertResponse[]> => {
    const url = '/alerts/red/pending/';
    const method = 'GET';

    return this.request<PendingRedAlertResponse[]>({
      url,
      method,
    });
  };

  acceptRedAlert = (alertId: string): APIRequest<AlarmResponse> => {
    const url = `/alerts/red/pending/${alertId}/`;
    const method = 'PUT';

    return this.request<AlarmResponse>({
      url,
      method,
    });
  };

  ignoreRedAlert = (alertId: string): APIRequest<void> => {
    const url = `/alerts/red/pending/${alertId}/`;
    const method = 'DELETE';

    return this.request<void>({
      url,
      method,
    });
  };

  completeRedAlert = (
    alertId: string,
    resolutionReason: ResolutionReasonFormValues
  ): APIRequest<CompletedRedAlertResponse> => {
    const url = `/alerts/red/accepted/${alertId}/`;
    const method = 'PUT';
    const data = {
      ...resolutionReason,
    };

    return this.request<CompletedRedAlertResponse>({
      url,
      method,
      data,
    });
  };

  loadResolutionReasons = (): APIRequest<ResolutionReasonResponse[]> => {
    const url = '/alerts/red/resolution-reasons/';
    const method = 'GET';

    return this.request<ResolutionReasonResponse[]>({
      url,
      method,
    });
  };
}

export default AlarmsAPI;
